import { useEffect, useMemo, useRef, useState } from "react";

import { AutocompleteField } from "@/components/ui/autocomplete-field";
import { ChipList } from "@/components/ui/chip-list";
import { MuiTypography } from "@/components/ui/mui-typography";
import { TreeSelect, type TreeSelectNode } from "@/components/ui/tree-select";
import { useCatalogLookupMenu, useCatalogTiers } from "@/hooks/catalog-admin";
import { useCatalogCategoryTree } from "@/hooks/catalog-items";
import {
  buildCategoryTree,
  categoryAncestorIds,
  filterCategoryTree,
  findCategory,
} from "@/lib/catalog-category-tree";
import {
  addManufacturerWrite,
  addTierWrite,
  categoryFilterWrite,
  removeManufacturerWrite,
  removeTierWrite,
  resolveIslandFilter,
  type IslandSearch,
  type IslandSearchPatch,
} from "@/lib/item-search-island";

// The Item Search island's filters (#895) — the rest of the donor's
// `SearchFilters` (`timmy/src/domains/items/islands/ItemSearchIsland/
// components/SearchFilters/`): `Filter items by:`, the Category, Manufacturer
// and EI Package (tier) filters, and `AppliedFilters` below them. It renders the cells of
// #811's filter-band grid after the search box, so the donor's one `Grid
// container` — `rowSpacing 1.5`, `columnSpacing {sm 2}` — spaces them all.
//
// **It holds no filter state.** Every control reads its value from the URL
// (`search`) and asks for a write (`onSearchChange`), which the host turns into
// a `replace` navigation merged into the rest (`islandSearchNavigation`). The
// only state here is the category menu's typed term, which narrows the menu,
// not the island.
//
// **Medium fields.** The island mounts its filters without `size`, so each is
// MUI's default — 56px — where the items sidebar passes `size='small'`.
//
// **The shared fields, fed as the catalog sidebar feeds them** (#767 R2): the
// category tree and the paged manufacturer menu. The menu names no chip: the
// donor's chips resolve against its object cache, which the menu's read never
// fills (#987), so the names come from the host (`knownManufacturers`). The tier
// list is the donor's `UncontrolledSelectField`, which draws the same
// autocomplete as the manufacturer's; its query ignores the term, so it is one
// page here and MUI's local filter is the only narrowing.

/** `xs 6 / xl 4` in the band's gap grid: half a row less half the 16px gap from
 *  `sm` (none below it), and a third less two thirds of two gaps from `xl`. */
const FILTER_CELL = "w-1/2 sm:w-[calc(50%-8px)] xl:w-[calc((100%-32px)/3)]";

interface ItemSearchFiltersProps {
  /** The island's URL keys. */
  search: IslandSearch;
  onSearchChange: (patch: IslandSearchPatch) => void;
  /**
   * The manufacturers the island's cache holds — the only names a chip takes.
   * The donor resolves `mfrs` against its object cache (`selectMfrMap`) and
   * drops an id the cache does not hold (#987): what the session's searches and
   * item reads carried, a deleted manufacturer's included (#964 N18), and the
   * picks and lookup writes made in the session.
   */
  knownManufacturers?: readonly { id: number; name: string }[];
  /**
   * A manufacturer picked from the menu, told before its URL write — the
   * donor's `updateItemsObjectCache` in `handleAddManufacturerFilter`, so the
   * host can name its chip at once. Not told for a repeat pick.
   */
  onManufacturerPick?: (manufacturer: { id: number; name: string }) => void;
}

export function ItemSearchFilters({
  search,
  onSearchChange,
  knownManufacturers = [],
  onManufacturerPick,
}: ItemSearchFiltersProps) {
  const [categoryTerm, setCategoryTerm] = useState("");

  const treeQuery = useCatalogCategoryTree();
  const manufacturerMenu = useCatalogLookupMenu("manufacturer");
  const tiersQuery = useCatalogTiers();

  const nodes = useMemo(() => treeQuery.data?.nodes ?? [], [treeQuery.data]);
  const tree: TreeSelectNode[] = useMemo(
    () => filterCategoryTree(buildCategoryTree(nodes), categoryTerm),
    [nodes, categoryTerm],
  );
  const urlCategory = search.category ?? "";
  // A slug the tree cannot name reads `All Categories`, as the donor's form
  // value is `''` when `selectCategoryBySlug` finds nothing — and is dropped,
  // below.
  const selectedCategory = useMemo(
    () => (urlCategory === "" ? undefined : nodes.find((node) => node.slug === urlCategory)),
    [nodes, urlCategory],
  );
  // **A slug the tree cannot name is dropped** (#964 N15), as the donor's two
  // form↔URL effects drop it (`CategoryFilter.tsx:26-28,37-81`). Its form
  // holds the URL's slug when the category cache names it, else `''`, and its
  // form→URL effect writes whenever that value differs from the URL's slug
  // *and* from its own previous value — `usePrevious`, undefined on the first
  // render. So the unnamed slug is deleted, `page=1` written, on arrival and on
  // a move from a named slug; a move to it from no category leaves it (the form
  // stays `''`), the field reading All Categories. Read once the tree has
  // answered, as the donor's filters mount after the island's metadata read.
  const treeSettled = treeQuery.data !== undefined || treeQuery.isError;
  const formSlug = selectedCategory?.slug ?? "";
  const previousFormSlug = useRef<string | undefined>(undefined);
  const onSearchChangeRef = useRef(onSearchChange);
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  });
  useEffect(() => {
    if (!treeSettled) return;
    const previous = previousFormSlug.current;
    previousFormSlug.current = formSlug;
    if (formSlug === urlCategory || formSlug === previous) return;
    const patch = categoryFilterWrite(urlCategory, formSlug);
    if (patch) onSearchChangeRef.current(patch);
  }, [treeSettled, formSlug, urlCategory]);
  const expandedByDefault = useMemo(
    () => (selectedCategory ? categoryAncestorIds(nodes, selectedCategory.id) : []),
    [nodes, selectedCategory],
  );

  const manufacturerNames = useMemo(
    () => new Map(knownManufacturers.map((row) => [row.id, { id: row.id, name: row.name }])),
    [knownManufacturers],
  );
  const selectedManufacturers = resolveIslandFilter(search.mfrs, manufacturerNames);

  // `selectAllTiers`: sorted by `value`.
  const tiers = useMemo(
    () => [...(tiersQuery.data?.tiers ?? [])].sort((a, b) => a.value - b.value),
    [tiersQuery.data],
  );
  const tiersById = useMemo(() => new Map(tiers.map((tier) => [tier.id, tier])), [tiers]);
  const selectedTiers = resolveIslandFilter(search.tiers, tiersById);

  const write = (patch: IslandSearchPatch | null) => {
    if (patch) onSearchChange(patch);
  };

  const showChips = selectedManufacturers.length > 0 || selectedTiers.length > 0;

  return (
    <>
      <div className="w-full">
        <MuiTypography variant="subtitle1" color="primary">
          Filter items by:
        </MuiTypography>
      </div>
      <div className={FILTER_CELL} data-testid="category-filter">
        <TreeSelect
          label="Category"
          size="medium"
          allowEmpty
          value={selectedCategory?.id ?? null}
          nodes={tree}
          onChange={(next) => {
            const slug = next === null ? "" : (findCategory(nodes, next)?.slug ?? "");
            write(categoryFilterWrite(urlCategory, slug));
          }}
          emptyLabel="All Categories"
          searchPlaceholder="Search categories"
          searchTerm={categoryTerm}
          onSearchTermChange={setCategoryTerm}
          expandedByDefault={expandedByDefault}
          // The donor's `FormControl disabled={… || isFetching …}`.
          loading={treeQuery.isFetching}
        />
      </div>
      <div className={FILTER_CELL} data-testid="manufacturer-filter">
        <AutocompleteField
          label="Manufacturer"
          size="medium"
          labelCount={selectedManufacturers.length}
          options={manufacturerMenu.options}
          onSelect={(option) => {
            const patch = addManufacturerWrite(selectedManufacturers, option);
            if (!patch) return;
            onManufacturerPick?.({ id: option.id, name: option.name });
            onSearchChange(patch);
          }}
          loading={manufacturerMenu.loading}
          failed={manufacturerMenu.failed}
          hasMore={manufacturerMenu.hasMore}
          onLoadMore={manufacturerMenu.onLoadMore}
          onSearchSettled={manufacturerMenu.onSearchSettled}
        />
      </div>
      <div className={FILTER_CELL} data-testid="tier-filter">
        <AutocompleteField
          label="EI Package"
          size="medium"
          labelCount={selectedTiers.length}
          options={tiers}
          onSelect={(option) => {
            const tier = tiersById.get(option.id);
            if (tier) write(addTierWrite(selectedTiers, tier));
          }}
          // `useGetTiersQueryFalcon` answers `isFetching: false` from the cache.
          loading={false}
        />
      </div>
      {search.itemSearch ? (
        <div className="w-full">
          <MuiTypography variant="subtitle1" color="primary">
            Results for: {search.itemSearch}
          </MuiTypography>
        </div>
      ) : null}
      {showChips ? (
        // `Stack direction='row' wrap` around two `ChipList`s, manufacturers
        // then tiers. The donor's chips each carry `mr 8 / mb 8`, the last
        // included (`chip-list.tsx:33-41`), so the lists sit 8px apart, the row
        // stands 40px over 32px chips, and a chip wraps where its trailing 8px
        // would overflow — the shared list's `margins` spacing.
        <div className="flex w-full flex-wrap" data-testid="item-search-applied-filters">
          <ChipList
            chips={selectedManufacturers.map((row) => ({ key: String(row.id), label: row.name }))}
            onRemove={(key) => write(removeManufacturerWrite(selectedManufacturers, Number(key)))}
            spacing="margins"
            data-testid="item-search-manufacturer-chips"
          />
          <ChipList
            chips={selectedTiers.map((row) => ({ key: String(row.id), label: row.name }))}
            onRemove={(key) => write(removeTierWrite(selectedTiers, Number(key)))}
            spacing="margins"
            data-testid="item-search-tier-chips"
          />
        </div>
      ) : null}
    </>
  );
}
